import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import { Close as CloseIcon } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import type { ANPRData } from "./ANPRTable";
import { formatDateDMY } from "../../utils/dateFormat.ts";
import React from "react";

/** Table-style row id, e.g. ANPR-001 (not raw API uuid). */
function formatAnprDisplayId(id: string): string {
  if (/^ANPR-\d{3}$/i.test(id ?? "")) return id;
  return id;
}

interface ViewANPRDialogProps {
  open: boolean;
  onClose: () => void;
  anpr: ANPRData | null;
  /** Same label as table ID column, e.g. ANPR-001 */
  displayId?: string | null;
}

const getCategoryColor = (category: string) => {
  switch (category.toLowerCase()) {
    case "staff":
      return { bgcolor: "#E6F4EA", color: "#1E8E3E" };
    case "visitor":
      return { bgcolor: "#E8F0FE", color: "#1967D2" };
    case "contractor":
      return { bgcolor: "#FEF7E0", color: "#F9AB00" };
    default:
      return { bgcolor: "#F1F3F4", color: "#5F6368" };
  }
};

export function ViewANPRDialog({
  open,
  onClose,
  anpr,
  displayId,
}: ViewANPRDialogProps) {
  if (!anpr) return null;

  const categoryStyle = getCategoryColor(anpr.category ?? "");
  const idLabel = displayId?.trim() || formatAnprDisplayId(anpr.id);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") onClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#F2F2F2",
          py: 2,
          px: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 500,
            color: "#212121",
          }}
        >
          ANPR Details
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#4A5565",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* License Plate */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                bgcolor: "#FFEB3B",
                border: "2px solid #212121",
                borderRadius: "4px",
                px: 3,
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: "24px",
                  fontWeight: 700,
                  color: "#212121",
                  letterSpacing: "4px",
                }}
              >
                {anpr.licensePlate}
              </Typography>
            </Box>
          </Box>

          {/* Details in 2-column grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#9E9E9E",
                  mb: 0.5,
                }}
              >
                ID
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  color: "#4A5565",
                }}
              >
                {idLabel}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#9E9E9E",
                  mb: 0.5,
                }}
              >
                Category
              </Typography>
              <Chip
                label={anpr.category || "—"}
                size="small"
                sx={{
                  bgcolor: categoryStyle.bgcolor,
                  color: categoryStyle.color,
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  height: 24,
                  borderRadius: "4px",
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#9E9E9E",
                  mb: 0.5,
                }}
              >
                Vehicle Owner
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  color: "#4A5565",
                }}
              >
                {anpr.vehicleOwner}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#9E9E9E",
                  mb: 0.5,
                }}
              >
                Permit Start
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  color: "#4A5565",
                }}
              >
                {formatDateDMY(anpr.permitStartDate)}
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#9E9E9E",
                  mb: 0.5,
                }}
              >
                Permit End
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  color: "#4A5565",
                }}
              >
                {formatDateDMY(anpr.permitEndDate)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#F2F2F2",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            bgcolor: "#2932E5",
            border: "0.8px solid #2932E5",
            borderRadius: "8px",
            color: "#fff",
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: 500,
            px: 3,
            "&:hover": {
              bgcolor: "#1f28b8",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
